import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { AlertErrorIcon } from '@/components/icon'
import { SimpleLogoPageContainer } from '@/components/shared/SimpleLogoPageContainer'
import {
  Box,
  Button,
  Checkbox,
  Field,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
} from '@/components/ui'
import { ApiError, useMutation } from '@/lib/api'
import { confirmSignIn } from '@/lib/auth'

const mfaFormSchema = z.object({
  code: z.string().min(1, '認証コードを入力してください'),
  rememberThisDevice: z.boolean().optional(),
})

type FormValue = z.infer<typeof mfaFormSchema>

export const MFAPage = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, errors },
  } = useForm<FormValue>({
    mode: 'onChange',
    resolver: zodResolver(mfaFormSchema),
    defaultValues: {
      code: '',
      rememberThisDevice: false,
    },
  })

  const navigate = useNavigate()

  const [apiError, setApiError] = useState<ApiError>()

  const { request: submitConfirmSignIn, isPending } = useMutation(
    async (data: FormValue) => {
      return confirmSignIn({
        code: data.code,
        rememberDevice: data.rememberThisDevice,
      })
    },
    {
      onSuccess: () => {
        navigate({
          to: '/trials',
        })
      },
      onError: setApiError,
    },
  )

  return (
    <SimpleLogoPageContainer>
      <Stack gap="12" maxW="520px" minW="320px" w="full" h="full">
        <Stack gap="2" align="center">
          <Heading as="h1">2段階認証ログイン</Heading>
          <Text fontWeight="normal">
            認証用アプリに表示されている、認証コードを入力してください。
          </Text>
        </Stack>
        <form onSubmit={handleSubmit(submitConfirmSignIn)}>
          <Stack gap="12">
            <FieldRoot
              display="grid"
              gap="4"
              gridTemplateColumns="max-content 1fr"
              alignItems="baseline"
              invalid={!!errors.code}
            >
              <FieldLabel fontSize="md" fontWeight="normal" color="text.sub">
                認証コード
              </FieldLabel>
              <Box>
                <Input
                  {...register('code')}
                  type="tel"
                  autoComplete="one-time-code"
                  placeholder="認証コードを入力してください"
                />
                <FieldErrorText>{errors.code?.message}</FieldErrorText>
              </Box>
            </FieldRoot>
            <Controller
              name="rememberThisDevice"
              control={control}
              render={({ field }) => (
                <Field>
                  <Checkbox
                    w="full"
                    justifyContent="center"
                    checked={field.value}
                    onCheckedChange={({ checked }) => field.onChange(checked)}
                  >
                    このデバイスを信頼済みデバイスとして記録する
                  </Checkbox>
                </Field>
              )}
            />
            {!!apiError && (
              <HStack color="text.error" gap="2" justify="center">
                <AlertErrorIcon />
                <Text whiteSpace="pre-wrap">{apiError.message}</Text>
              </HStack>
            )}
            <HStack justify="center" colorPalette="blue" gap="6">
              <Link to="/login">
                <Button as="span" variant="outline" w="100px">
                  キャンセル
                </Button>
              </Link>
              <Button
                type="submit"
                variant="solid"
                w="100px"
                loading={isPending}
                disabled={!isValid}
              >
                ログイン
              </Button>
            </HStack>
          </Stack>
        </form>
      </Stack>
    </SimpleLogoPageContainer>
  )
}
